// Defaults de la configuración de apariencia del TPV camarero.
//
// **Regla de oro**: estos valores deben reflejar EXACTAMENTE el look actual
// del TPV camarero (lo que ven los restaurantes hoy). Cualquier restaurante
// que no haya configurado nunca la apariencia debe ver lo mismo después de
// desplegar esta feature — el campo `User.TpvLayoutSettingsJson` viene null
// y el hook `useTpvLayout` mergea contra estos defaults.
//
// Si añades un campo nuevo aquí, NUNCA rompe a usuarios existentes: el
// merge solo añade el nuevo campo con su default; el JSON guardado en BD
// no necesita migrarse.

export const TPV_LAYOUT_DEFAULTS = {
  // ─── Tema ────────────────────────────────────────────────────────────────
  // Solo afecta al TPV camarero (DarkKitchen + sus hijos). El resto de la
  // app sigue con el themeMode global del slice settings.
  themeMode: "light", // 'light' | 'dark'
  themeDirection: "ltr", // 'ltr' | 'rtl'

  // ─── Tipografía ──────────────────────────────────────────────────────────
  // fontFamily: una de las pre-cargadas en index.html. "default" = no
  // override (usa Be Vietnam global). El resto del string DEBE coincidir
  // con un valor de TPV_FONT_OPTIONS abajo.
  fontFamily: "default",
  // Multiplicador global del tamaño de fuente del TPV. Se aplica al
  // theme.typography.htmlFontSize (16 * fontScale). Rango [0.85, 1.4].
  fontScale: 1.0,

  // Capitalización del nombre en cards. "default" deja el texto tal cual
  // viene del backend; "upper" fuerza MAYÚSCULAS; "lower" minúsculas.
  // Independiente productos vs categorías para permitir, p.ej., categorías
  // en mayúsculas como titulares y productos en minúsculas como cuerpo.
  productTextCase: "default", // "default" | "upper" | "lower"
  categoryTextCase: "default",

  // ─── Presets de cards ────────────────────────────────────────────────────
  // Estos presets cambian VARIOS campos a la vez en el panel UI (atajo
  // visual); los campos finos siguen sobrescribiendo el preset si el
  // usuario los toca después de aplicarlo.
  productCardPreset: "auto", // 'auto'|'photo'|'color'|'minimal'|'classic'
  categoryCardPreset: "auto", // 'auto'|'photo'|'color'|'minimal'|'iconColor'

  // ─── Cards finos ─────────────────────────────────────────────────────────
  // Si false, ignora el campo image del producto/categoría y trata la card
  // como sin foto (color o blanca según el color-fill correspondiente).
  showProductPhotos: true,
  showCategoryPhotos: true,
  // Color rellena la card — INDEPENDIENTE productos vs categorías para que
  // el restaurante pueda, p.ej., teñir categorías y dejar productos blancos.
  // `colorFillsCard` legacy se mantiene como fallback común cuando los
  // específicos son null (cuentas previas no se rompen).
  colorFillsCard: true, // legacy — fallback común
  productColorFillsCard: null, // null = usa legacy
  categoryColorFillsCard: null, // null = usa legacy

  // Color de fondo por DEFECTO cuando un producto/categoría no tiene foto
  // ni color asignado. null = comportamiento histórico (blanco/surface del
  // tema). Si se fija un hex, todas las cards "vacías" lo usan como base.
  // Independiente por tipo para permitir, p.ej., categorías color crema y
  // productos blancos.
  productCardDefaultColor: null, // null | "#hex"
  categoryCardDefaultColor: null, // null | "#hex"

  // ─── Iconos en el card de producto ───────────────────────────────────────
  showNotesIcon: true,
  showInfoIcon: true,
  showDeleteIcon: true,
  showStockChip: true,
  // Icono "3 puntos" arriba a la derecha que abre Editar / Desactivar.
  // Algunos restaurantes prefieren ocultarlo en los TPVs de los camareros
  // para que sólo el admin pueda editar productos desde Gestión.
  showOptionsIcon: true,

  // ─── Badge de precio en la card de producto ──────────────────────────────
  // Look histórico: chip rojo (#FF4842 del Label color="error") en la
  // esquina top-left, texto blanco UPPERCASE, fontSize del Typography
  // body2 (~14px mobile) / 0.68rem (~11px desktop). Cualquiera de estos
  // valores puede sobrescribirse desde Apariencia TPV > Cards > Precio.
  productPriceBg: null, // null | "#hex" — null = error.main del tema
  productPriceColor: null, // null | "#hex" — null = blanco
  productPricePosition: "top-left", // "top-left" | "top-right" | "bottom-left" | "bottom-right"
  productPriceFontSize: null, // null | 9..18 — null = look histórico
  productPriceTextCase: "default", // "default" | "upper" | "lower"
  // Tamaño de los iconos del card de producto (px). Actual = 17.
  cardIconSize: 17,

  // ─── Densidad ────────────────────────────────────────────────────────────
  // Multiplicador del alto del card. El legacy `cardHeightScale` rige los
  // dos tipos (producto + categoría) si los específicos están en null.
  // Los específicos permiten al restaurante hacer las categorías más
  // bajas (tira fina arriba) y los productos más altos (touch fácil)
  // sin sacrificar uno por el otro.
  cardHeightScale: 1.0, // legacy — fallback común
  productCardHeightScale: null, // null | 0.6..1.5
  categoryCardHeightScale: null, // null | 0.6..1.5
  mesaCardHeightScale: null, // null | 0.6..1.5 (alto de la tile de mesa)

  // Ancho de la tarjeta como fracción de su slot del grid. 1.0 = ocupa
  // toda la columna que le da el `xs`. <1.0 = la card queda más estrecha
  // centrada dentro del slot (margen lateral automático). Permite
  // diferenciar visualmente productos vs categorías sin tocar el nº de
  // cols. Rango [0.6, 1.0]. null = 1.0 (comportamiento histórico).
  productCardWidthScale: null,
  categoryCardWidthScale: null,
  mesaCardWidthScale: null,
  // Columnas en desktop/mobile — separadas por tipo (categorías vs productos)
  // para que el restaurante pueda enseñar más categorías por fila sin
  // sacrificar tamaño de productos o viceversa. null = auto (mismos
  // defaults históricos: 5 sub-lg / 7 lg+ desktop, 3 mobile).
  // Los campos `desktopColumns` y `mobileColumns` siguen leyéndose como
  // *fallback* de los específicos si el restaurante los configuró antes.
  desktopColumns: null, // legacy — fallback común
  mobileColumns: null, // legacy — fallback común
  categoryDesktopColumns: null, // null | 4..8
  productDesktopColumns: null, // null | 4..8
  categoryMobileColumns: null, // null | 2..4
  productMobileColumns: null, // null | 2..4

  // ─── Densidad de la vista de MESAS (independiente de la carta) ──────────
  // null = auto (2/3/4/6 mesas por fila según breakpoint, comportamiento
  // histórico). Cuando se fija un número, se usa en TODOS los breakpoints
  // del lado correspondiente.
  mesasDesktopColumns: null, // null | 3 | 4 | 5 | 6 | 8
  mesasMobileColumns: null, // null | 2 | 3 | 4

  // ─── Colores de los estados de la mesa (vista plano) ────────────────────
  // Personalización del FONDO del tile según estado. null = preset histórico
  // (verde libre #22c55e / rojo ocupada #ef4444 / gris no disponible #94a3b8).
  // Los hex se respetan tal cual; el texto sigue siendo blanco para
  // garantizar contraste con cualquier elección razonable del restaurante.
  mesaColors: {
    free: null, // null | "#hex" — mesa libre / sin sesión
    occupied: null, // null | "#hex" — mesa con sesión activa
    unavailable: null, // null | "#hex" — caja cerrada / QR desactivado
  },

  // ─── Alerta visual cuando la mesa tiene productos LISTOS (DONE) ─────────
  // Controla el "parpadeo" (borde verde lima + halo + scale sutil) que se
  // activa cuando la cocina marca productos como ready. La intensidad
  // afecta al tamaño del halo, la velocidad del ciclo y al scale:
  //   - "subtle":  apenas se percibe (cliente con muchas mesas en DONE)
  //   - "normal":  visible desde lejos sin ser intrusivo (recomendado)
  //   - "intense": agresivo, halo grande + ciclo rápido
  // Si `enabled=false` la mesa con DONE se muestra sin animación (el
  // restaurante puede preferir usar solo los badges).
  mesaReadyAlert: {
    enabled: true,
    color: null, // null | "#hex" — null = #22c55e (verde lima)
    intensity: "normal", // "subtle" | "normal" | "intense"
  },

  // ─── Colores de los badges secundarios sobre el tile ─────────────────────
  // Iconos pequeños que aparecen junto a la mesa indicando estados parciales.
  // null = preset histórico de tones (info/success/warning). Independientes
  // entre sí — el restaurante puede, p.ej., poner "sin comandar" en rojo
  // para que destaque más que el ámbar de "en cocina".
  mesaBadgeColors: {
    inProgress: null, // En preparación (cocina cocinando) — default #f59e0b ámbar
    ready: null,      // Cocina lista (icono extra al pulse) — default #10b981 verde
    draft: null,      // Productos sin comandar — default #0ea5e9 azul
    payment: null,    // Pago solicitado por cliente — default #f59e0b ámbar
    waiter: null,     // Llamada al camarero — default #0ea5e9 azul
  },

  // ─── Paginación de productos (desktop) ──────────────────────────────────
  // Nº de FILAS de productos por página en la carta del camarero (desktop).
  // El total visible por página = filas × columnas (productDesktopColumns).
  // null = auto: calcula las filas que caben en el viewport (comportamiento
  // histórico, evita huecos vacíos). Cuando se fija un número, el camarero
  // siempre ve exactamente esa cantidad y la página corta antes de hacer
  // scroll — útil para que el reparto sea predecible entre páginas.
  productPageRows: null, // null | 2..15

  // ─── CartWidget (barra inferior mobile) ─────────────────────────────────
  cartWidget: {
    // Mostrar la franja "Total mesa + X sin comandar" que se quitó por
    // petición previa. Quien la quiera de vuelta puede activarla aquí.
    showTotalBar: false,
    // Qué acciones secundarias enseñar (Cocinar siempre se muestra como CTA).
    showSave: true,
    showPrint: true,
    showProducts: true,
    showPay: true,
    // Master switch de "Etiquetas de curso" (Primeros/Segundos/…).
    // Al activarlo (con courseLabels definidos en Gestión), la carta
    // camarero muestra el selector sticky arriba, chip del curso bajo
    // cada línea del cart, FAB de Marchas y (en cocina) comanda
    // agrupada por curso + mini-ticket al marchar. Antes vivía en
    // `User.ShowCourseSelectorInCamarero` (Ajustes → Empleados) — se
    // migró aquí para que aparezca en el preview mobile/desktop junto
    // a los demás controles de la barra.
    showCourseSelector: false,
  },

  // ─── Action bar de la mesa abierta (top de ButtonsTop desktop) ──────────
  // Tamaño común a todos los botones (Cerrar mesa / Imprimir / Pagar /
  // Cajón / Reservas / Otros). El color por defecto sigue siendo el preset
  // por `kind` (red/yellow/ghost); por botón se pueden override `bg` y
  // `color` con un hex personalizado dejándolos en null = usa el preset.
  // Los toggles de permisos NO se tocan aquí (siguen condicionando).
  actionBarButtonHeight: 48, // px, 40..56
  actionBarButtonFontSize: 14, // px, 12..18
  actionBarButtonMinWidth: 130, // px, 90..220 (minWidth + grow auto)
  actionBarButtonTextCase: "default", // "default" | "upper" | "lower"
  // Mapa { buttonKey: { bg: "#hex"|null, color: "#hex"|null } }.
  // buttonKey ∈ { close, print, pay, cashbox, reservas, others }.
  actionBarButtons: {
    close: { bg: null, color: null },
    print: { bg: null, color: null },
    pay: { bg: null, color: null },
    cashbox: { bg: null, color: null },
    reservas: { bg: null, color: null },
    others: { bg: null, color: null },
  },

  // ─── Action bar de la carta del camarero (TpvPayment desktop) ───────────
  // Misma idea que arriba pero para los 9 botones del header DENTRO de la
  // mesa: Volver / Cambiar / Cajón / Rotar / Comensales / Imprimir / Pagar /
  // Guardar / Comandar. Por defecto todos son "ghost" salvo "command" que
  // es el CTA amarillo. Tamaño común propio (la barra vive en el panel
  // derecho, no comparte espacio con ButtonsTop).
  cartaButtonHeight: 42, // px, 36..56
  cartaButtonFontSize: 13, // px, 11..18
  cartaButtonMinWidth: 0, // px, 0..160 (0 = auto al contenido)
  cartaButtonTextCase: "default", // "default" | "upper" | "lower"
  // buttonKey ∈ { back, switch, cashbox, rotate, diners, print, pay, save, command }.
  cartaButtons: {
    back: { bg: null, color: null },
    switch: { bg: null, color: null },
    cashbox: { bg: null, color: null },
    rotate: { bg: null, color: null },
    diners: { bg: null, color: null },
    print: { bg: null, color: null },
    pay: { bg: null, color: null },
    save: { bg: null, color: null },
    command: { bg: null, color: null },
  },

  // ─── Tabs de zonas (header de la vista de mesas) ────────────────────────
  // Las zonas vienen de la BD; aquí solo se personaliza su apariencia y el
  // ORDEN visual (sin tocar la entidad Zona). `zonesOrder` array de ids:
  // las zonas listadas aparecen primero en ese orden, las restantes después
  // en orden natural.
  zonesStripHeight: 32, // px, 28..44
  zonesStripFontSize: 12, // px, 10..16
  zonesTextCase: "upper", // "default" | "upper" | "lower" — histórico = upper
  zonesOrder: null, // null | number[] (zone ids)
};

// Opciones disponibles para el desplegable de fuentes. El value "default"
// significa "no override" → usa Be Vietnam del theme global.
//
// IMPORTANTE: cualquier value distinto de "default" tiene que estar
// pre-cargado vía <link> en public/index.html, si no el navegador hará
// fallback a sans-serif.
export const TPV_FONT_OPTIONS = [
  { value: "default", label: "Be Vietnam (por defecto)", stack: null },
  { value: "Inter", label: "Inter (limpia, moderna)", stack: "Inter, sans-serif" },
  { value: "Roboto", label: "Roboto (estándar)", stack: "Roboto, sans-serif" },
  { value: "Nunito", label: "Nunito (redondeada)", stack: "Nunito, sans-serif" },
  { value: "Open Sans", label: "Open Sans (clásica)", stack: "'Open Sans', sans-serif" },
  { value: "system", label: "Sistema (rápida)", stack: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif" },
];

// Resuelve un value de fontFamily a un font-stack CSS. null = no override.
export function resolveFontStack(value) {
  const opt = TPV_FONT_OPTIONS.find((o) => o.value === value);
  return opt?.stack || null;
}

// Devuelve el valor CSS de `text-transform` para el nombre del
// producto/categoría según el draft. "default" → null (sin override) para
// que cada card respete su capitalización original; "upper" → uppercase;
// "lower" → lowercase. Usado en ProductItem y CategoryTile.
export function resolveTextCase(layout, target /* 'category' | 'product' */) {
  if (!layout) return null;
  const v =
    target === "category" ? layout.categoryTextCase : layout.productTextCase;
  if (v === "upper") return "uppercase";
  if (v === "lower") return "lowercase";
  return null; // default → no override
}

// Resuelve qué `cardHeightScale` aplicar a cada tipo. El específico gana;
// si está en null, cae al legacy común; si ese también es null, 1.0.
// target = 'category' | 'product' | 'mesa'.
export function resolveCardHeightScale(layout, target) {
  if (!layout) return 1;
  const k =
    target === "category"
      ? "categoryCardHeightScale"
      : target === "mesa"
        ? "mesaCardHeightScale"
        : "productCardHeightScale";
  return Number(layout[k] ?? layout.cardHeightScale ?? 1);
}

// Ancho como fracción del slot del grid (0.6..1.0). null → 1.0
// (la card ocupa todo el slot, comportamiento histórico).
export function resolveCardWidthScale(layout, target) {
  if (!layout) return 1;
  const k =
    target === "category"
      ? "categoryCardWidthScale"
      : target === "mesa"
        ? "mesaCardWidthScale"
        : "productCardWidthScale";
  return Number(layout[k] ?? 1);
}

// Resuelve cuántas columnas usar en cada vista. Soporta el legacy
// `desktopColumns/mobileColumns` (común carta = categorías+productos) y
// los nuevos específicos por tipo. Devuelve null si no hay nada fijado
// (el caller usa entonces sus breakpoints históricos auto).
export function resolveColumns(layout, target /* 'category' | 'product' */, mode /* 'desktop' | 'mobile' */) {
  if (!layout) return null;
  const isDesktop = mode === "desktop";
  if (target === "category") {
    return (
      (isDesktop ? layout.categoryDesktopColumns : layout.categoryMobileColumns) ??
      (isDesktop ? layout.desktopColumns : layout.mobileColumns) ??
      null
    );
  }
  return (
    (isDesktop ? layout.productDesktopColumns : layout.productMobileColumns) ??
    (isDesktop ? layout.desktopColumns : layout.mobileColumns) ??
    null
  );
}

// Merge profundo (1 nivel) de la config guardada (puede ser null/parcial/
// con campos extra) contra los defaults. Garantiza que el resultado siempre
// tiene TODOS los campos y dentro del rango esperado.
export function mergeTpvLayout(saved) {
  if (!saved || typeof saved !== "object") return TPV_LAYOUT_DEFAULTS;
  return {
    ...TPV_LAYOUT_DEFAULTS,
    ...saved,
    cartWidget: {
      ...TPV_LAYOUT_DEFAULTS.cartWidget,
      ...(saved.cartWidget || {}),
    },
    actionBarButtons: {
      ...TPV_LAYOUT_DEFAULTS.actionBarButtons,
      ...(saved.actionBarButtons || {}),
    },
    cartaButtons: {
      ...TPV_LAYOUT_DEFAULTS.cartaButtons,
      ...(saved.cartaButtons || {}),
    },
    mesaColors: {
      ...TPV_LAYOUT_DEFAULTS.mesaColors,
      ...(saved.mesaColors || {}),
    },
    mesaReadyAlert: {
      ...TPV_LAYOUT_DEFAULTS.mesaReadyAlert,
      ...(saved.mesaReadyAlert || {}),
    },
    mesaBadgeColors: {
      ...TPV_LAYOUT_DEFAULTS.mesaBadgeColors,
      ...(saved.mesaBadgeColors || {}),
    },
  };
}

// Clamps básicos al guardar (evita que un valor fuera de rango deje el TPV
// inusable). Los dispatchers del panel también validan en UI pero esta es
// la última línea.
export function sanitizeTpvLayout(layout) {
  const m = mergeTpvLayout(layout);
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  return {
    ...m,
    // Rangos más amplios que antes (eran 0.85-1.4 / 0.85-1.3) — los
    // camareros querían poder apretar mucho más para meter más densidad.
    // Por debajo de 0.6 las cards quedan ilegibles; por encima de 1.6 el
    // panel y el TPV se desbordan.
    fontScale: clamp(Number(m.fontScale) || 1, 0.75, 1.6),
    cardHeightScale: clamp(Number(m.cardHeightScale) || 1, 0.6, 1.5),
    productCardHeightScale:
      m.productCardHeightScale == null
        ? null
        : clamp(Number(m.productCardHeightScale), 0.6, 1.5),
    categoryCardHeightScale:
      m.categoryCardHeightScale == null
        ? null
        : clamp(Number(m.categoryCardHeightScale), 0.6, 1.5),
    mesaCardHeightScale:
      m.mesaCardHeightScale == null
        ? null
        : clamp(Number(m.mesaCardHeightScale), 0.6, 1.5),
    productCardWidthScale:
      m.productCardWidthScale == null
        ? null
        : clamp(Number(m.productCardWidthScale), 0.6, 1),
    categoryCardWidthScale:
      m.categoryCardWidthScale == null
        ? null
        : clamp(Number(m.categoryCardWidthScale), 0.6, 1),
    mesaCardWidthScale:
      m.mesaCardWidthScale == null
        ? null
        : clamp(Number(m.mesaCardWidthScale), 0.6, 1),
    cardIconSize: clamp(Number(m.cardIconSize) || 17, 12, 28),
    desktopColumns:
      m.desktopColumns == null ? null : clamp(Number(m.desktopColumns), 4, 8),
    mobileColumns:
      m.mobileColumns == null ? null : clamp(Number(m.mobileColumns), 2, 4),
    categoryDesktopColumns:
      m.categoryDesktopColumns == null
        ? null
        : clamp(Number(m.categoryDesktopColumns), 4, 8),
    productDesktopColumns:
      m.productDesktopColumns == null
        ? null
        : clamp(Number(m.productDesktopColumns), 4, 8),
    categoryMobileColumns:
      m.categoryMobileColumns == null
        ? null
        : clamp(Number(m.categoryMobileColumns), 2, 4),
    productMobileColumns:
      m.productMobileColumns == null
        ? null
        : clamp(Number(m.productMobileColumns), 2, 4),
    mesasDesktopColumns:
      m.mesasDesktopColumns == null
        ? null
        : clamp(Number(m.mesasDesktopColumns), 3, 8),
    mesasMobileColumns:
      m.mesasMobileColumns == null
        ? null
        : clamp(Number(m.mesasMobileColumns), 2, 4),
    productPageRows:
      m.productPageRows == null
        ? null
        : clamp(Number(m.productPageRows), 2, 15),
    actionBarButtonHeight: clamp(Number(m.actionBarButtonHeight) || 48, 40, 56),
    actionBarButtonFontSize: clamp(
      Number(m.actionBarButtonFontSize) || 14,
      12,
      18,
    ),
    actionBarButtonMinWidth: clamp(
      Number(m.actionBarButtonMinWidth) || 130,
      90,
      220,
    ),
    actionBarButtonTextCase: ["default", "upper", "lower"].includes(
      m.actionBarButtonTextCase,
    )
      ? m.actionBarButtonTextCase
      : "default",
    actionBarButtons: sanitizeActionBarButtons(m.actionBarButtons),
    cartaButtonHeight: clamp(Number(m.cartaButtonHeight) || 42, 36, 56),
    cartaButtonFontSize: clamp(Number(m.cartaButtonFontSize) || 13, 11, 18),
    cartaButtonMinWidth: clamp(
      Number(m.cartaButtonMinWidth) == null ? 0 : Number(m.cartaButtonMinWidth),
      0,
      160,
    ),
    cartaButtonTextCase: ["default", "upper", "lower"].includes(
      m.cartaButtonTextCase,
    )
      ? m.cartaButtonTextCase
      : "default",
    cartaButtons: sanitizeCartaButtons(m.cartaButtons),
    productCardDefaultColor: isHexColor(m.productCardDefaultColor)
      ? m.productCardDefaultColor.trim()
      : null,
    categoryCardDefaultColor: isHexColor(m.categoryCardDefaultColor)
      ? m.categoryCardDefaultColor.trim()
      : null,
    productPriceBg: isHexColor(m.productPriceBg)
      ? m.productPriceBg.trim()
      : null,
    productPriceColor: isHexColor(m.productPriceColor)
      ? m.productPriceColor.trim()
      : null,
    productPricePosition: [
      "top-left",
      "top-right",
      "bottom-left",
      "bottom-right",
    ].includes(m.productPricePosition)
      ? m.productPricePosition
      : "top-left",
    productPriceFontSize:
      m.productPriceFontSize == null
        ? null
        : clamp(Number(m.productPriceFontSize), 9, 18),
    productPriceTextCase: ["default", "upper", "lower"].includes(
      m.productPriceTextCase,
    )
      ? m.productPriceTextCase
      : "default",
    mesaColors: sanitizeMesaColors(m.mesaColors),
    mesaReadyAlert: {
      enabled: m.mesaReadyAlert?.enabled !== false,
      color: isHexColor(m.mesaReadyAlert?.color)
        ? m.mesaReadyAlert.color.trim()
        : null,
      intensity: ["subtle", "normal", "intense"].includes(
        m.mesaReadyAlert?.intensity,
      )
        ? m.mesaReadyAlert.intensity
        : "normal",
    },
    mesaBadgeColors: sanitizeMesaBadgeColors(m.mesaBadgeColors),
    zonesStripHeight: clamp(Number(m.zonesStripHeight) || 32, 28, 44),
    zonesStripFontSize: clamp(Number(m.zonesStripFontSize) || 12, 10, 16),
    zonesTextCase: ["default", "upper", "lower"].includes(m.zonesTextCase)
      ? m.zonesTextCase
      : "upper",
    zonesOrder: Array.isArray(m.zonesOrder)
      ? m.zonesOrder
          .map((x) => Number(x))
          .filter((x) => Number.isFinite(x) && x > 0)
      : null,
  };
}

// Limpia el mapa de colores por botón: descarta keys desconocidas y valores
// que no parecen hex CSS válidos (#xxx o #xxxxxx). null/empty se preservan
// como "usa preset".
const ACTION_BAR_KEYS = ["close", "print", "pay", "cashbox", "reservas", "others"];
const CARTA_BAR_KEYS = [
  "back",
  "switch",
  "cashbox",
  "rotate",
  "diners",
  "print",
  "pay",
  "save",
  "command",
];
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
function isHexColor(v) {
  return typeof v === "string" && HEX_RE.test(v.trim());
}
function sanitizeActionBarButtons(raw) {
  const out = {};
  for (const k of ACTION_BAR_KEYS) {
    const entry = raw && typeof raw === "object" ? raw[k] : null;
    out[k] = {
      bg: isHexColor(entry?.bg) ? entry.bg.trim() : null,
      color: isHexColor(entry?.color) ? entry.color.trim() : null,
    };
  }
  return out;
}
function sanitizeCartaButtons(raw) {
  const out = {};
  for (const k of CARTA_BAR_KEYS) {
    const entry = raw && typeof raw === "object" ? raw[k] : null;
    out[k] = {
      bg: isHexColor(entry?.bg) ? entry.bg.trim() : null,
      color: isHexColor(entry?.color) ? entry.color.trim() : null,
    };
  }
  return out;
}

const MESA_COLOR_KEYS = ["free", "occupied", "unavailable"];
function sanitizeMesaColors(raw) {
  const out = {};
  for (const k of MESA_COLOR_KEYS) {
    const v = raw && typeof raw === "object" ? raw[k] : null;
    out[k] = isHexColor(v) ? v.trim() : null;
  }
  return out;
}

const MESA_BADGE_KEYS = ["inProgress", "ready", "draft", "payment", "waiter"];
function sanitizeMesaBadgeColors(raw) {
  const out = {};
  for (const k of MESA_BADGE_KEYS) {
    const v = raw && typeof raw === "object" ? raw[k] : null;
    out[k] = isHexColor(v) ? v.trim() : null;
  }
  return out;
}

// Resuelve cuántas FILAS por página usar en la grid de productos desktop.
// Devuelve null si está en auto (el caller calcula según viewport). Si está
// fijado, devuelve el número entero. Encapsulado aquí para que ProductList
// no tenga que conocer el nombre exacto del campo.
export function resolveProductPageRows(layout) {
  if (!layout) return null;
  const v = layout.productPageRows;
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 2) return null;
  return Math.floor(n);
}

// Override de color por botón de la action bar superior. Devuelve
// `{ bg, color }` con strings hex o null cuando no hay override (el caller
// usa el preset por defecto del `kind`). buttonKey ∈ close|print|pay|
// cashbox|reservas|others.
export function resolveActionBarButtonOverride(layout, buttonKey) {
  if (!layout || !buttonKey) return { bg: null, color: null };
  const entry = layout.actionBarButtons?.[buttonKey];
  if (!entry) return { bg: null, color: null };
  return {
    bg: typeof entry.bg === "string" && entry.bg.length ? entry.bg : null,
    color:
      typeof entry.color === "string" && entry.color.length ? entry.color : null,
  };
}

// Mismo patrón para la action bar de DENTRO de la mesa (TpvPayment).
// buttonKey ∈ back|switch|cashbox|rotate|diners|print|pay|save|command.
export function resolveCartaButtonOverride(layout, buttonKey) {
  if (!layout || !buttonKey) return { bg: null, color: null };
  const entry = layout.cartaButtons?.[buttonKey];
  if (!entry) return { bg: null, color: null };
  return {
    bg: typeof entry.bg === "string" && entry.bg.length ? entry.bg : null,
    color:
      typeof entry.color === "string" && entry.color.length ? entry.color : null,
  };
}

// Devuelve los 4 lados CSS según la posición elegida para el badge de
// precio. Las cards del TPV tienen los offsets a 4px del borde.
export function resolvePricePosition(layout) {
  const p = layout?.productPricePosition || "top-left";
  const OFF = 4;
  const out = { top: "auto", right: "auto", bottom: "auto", left: "auto" };
  if (p === "top-right") {
    out.top = OFF;
    out.right = OFF;
  } else if (p === "bottom-left") {
    out.bottom = OFF;
    out.left = OFF;
  } else if (p === "bottom-right") {
    out.bottom = OFF;
    out.right = OFF;
  } else {
    out.top = OFF;
    out.left = OFF;
  }
  return out;
}

// Capitalización del texto del badge de precio. Mismo patrón que zonas/
// botones. Default = "default" (no override, mantiene el `uppercase`
// hardcoded del Label si nadie toca nada). Usado en ProductItem.
export function resolvePriceTextCase(layout) {
  const v = layout?.productPriceTextCase;
  if (v === "upper") return "uppercase";
  if (v === "lower") return "lowercase";
  if (v === "default") return null;
  return null;
}

// Resuelve el color de fondo del tile de mesa según estado, respetando
// los overrides de `layout.mesaColors`. Devuelve siempre un hex válido —
// los defaults coinciden con los hardcoded en MesaTile original
// (free=verde, occupied=rojo, unavailable=gris).
export function resolveMesaStatusColor(layout, status) {
  const DEFAULTS = {
    free: "#22c55e",
    occupied: "#ef4444",
    unavailable: "#94a3b8",
  };
  const fallback = DEFAULTS[status] || DEFAULTS.unavailable;
  const v = layout?.mesaColors?.[status];
  return typeof v === "string" && v.length ? v : fallback;
}

// Resuelve la configuración de la alerta de mesa con productos READY
// (parpadeo + halo). Devuelve los parámetros listos para usar en
// framer-motion: { enabled, color, scaleMax, haloMax, duration, repeatDelay }.
export function resolveMesaReadyAlert(layout) {
  const cfg = layout?.mesaReadyAlert || {};
  const enabled = cfg.enabled !== false; // default true
  const color = typeof cfg.color === "string" && cfg.color.length
    ? cfg.color
    : "#22c55e";
  const intensity = ["subtle", "normal", "intense"].includes(cfg.intensity)
    ? cfg.intensity
    : "normal";

  const PRESETS = {
    subtle: { scaleMax: 1.015, ringMin: 3, ringMax: 4, haloMax: 10, duration: 1.4, repeatDelay: 0.4 },
    normal: { scaleMax: 1.03, ringMin: 4, ringMax: 5, haloMax: 22, duration: 1.0, repeatDelay: 0.1 },
    intense: { scaleMax: 1.06, ringMin: 5, ringMax: 7, haloMax: 32, duration: 0.8, repeatDelay: 0 },
  };
  return { enabled, color, intensity, ...PRESETS[intensity] };
}

// Color del badge secundario de mesa (los iconos pequeños que indican
// "en preparación", "sin comandar", etc.). target ∈ inProgress | ready |
// draft | payment | waiter. Devuelve hex válido — los defaults son los
// del mapa de tones histórico (warning ámbar / info azul / success verde).
export function resolveMesaBadgeColor(layout, target) {
  const DEFAULTS = {
    inProgress: "#f59e0b",
    ready: "#10b981",
    draft: "#0ea5e9",
    payment: "#f59e0b",
    waiter: "#0ea5e9",
  };
  const fallback = DEFAULTS[target] || "#0ea5e9";
  const v = layout?.mesaBadgeColors?.[target];
  return typeof v === "string" && v.length ? v : fallback;
}

// Color por defecto para una card sin foto NI color asignado. Devuelve un
// hex CSS o null (caller usa entonces el surface del tema, comportamiento
// histórico). target = 'category' | 'product'.
export function resolveCardDefaultColor(layout, target) {
  if (!layout) return null;
  const v =
    target === "category"
      ? layout.categoryCardDefaultColor
      : layout.productCardDefaultColor;
  if (typeof v === "string" && v.length) return v;
  return null;
}

// Capitalización para tabs de zonas (mismo patrón que productos/categorías
// pero con default = "upper" para mantener el comportamiento histórico).
export function resolveZonesTextCase(layout) {
  const v = layout?.zonesTextCase;
  if (v === "upper") return "uppercase";
  if (v === "lower") return "lowercase";
  if (v === "default") return null;
  return "uppercase"; // fallback histórico
}

// Capitalización para los textos de los botones de las action bars.
// target = "actionbar" (ButtonsTop) | "carta" (TpvPayment). Default por
// barra = "default" (no override; el botón usa su `text-transform: none`
// original, que es el look histórico de ambas barras).
export function resolveButtonTextCase(layout, target) {
  const v =
    target === "carta"
      ? layout?.cartaButtonTextCase
      : layout?.actionBarButtonTextCase;
  if (v === "upper") return "uppercase";
  if (v === "lower") return "lowercase";
  return null; // "default" / undefined → sin override
}

// Color-fill por tipo (producto vs categoría). El específico gana; si está
// en null cae al legacy común `colorFillsCard`; si ese también es undefined,
// true (comportamiento histórico). target = 'category' | 'product'.
export function resolveColorFillsCard(layout, target) {
  if (!layout) return true;
  const specific =
    target === "category"
      ? layout.categoryColorFillsCard
      : layout.productColorFillsCard;
  if (typeof specific === "boolean") return specific;
  return layout.colorFillsCard !== false;
}

// Reordena un array de zonas según `layout.zonesOrder` (array de ids). Las
// zonas presentes en `zonesOrder` aparecen primero en ese orden; las que no
// estén, se concatenan al final preservando su orden original. Si no hay
// `zonesOrder` (null/vacío), devuelve el array tal cual (sin copia).
export function applyZonesOrder(layout, zonas) {
  const order = layout?.zonesOrder;
  if (!Array.isArray(order) || order.length === 0) return zonas || [];
  if (!Array.isArray(zonas) || zonas.length === 0) return zonas || [];
  const indexById = new Map(order.map((id, i) => [Number(id), i]));
  const inOrder = [];
  const rest = [];
  for (const z of zonas) {
    if (indexById.has(Number(z.id))) inOrder.push(z);
    else rest.push(z);
  }
  inOrder.sort(
    (a, b) => indexById.get(Number(a.id)) - indexById.get(Number(b.id)),
  );
  return inOrder.concat(rest);
}
